import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sprout, Wheat, Bird, Mountain, Flame, Users, BookOpen,
  Feather, Compass, Tractor, Leaf, Landmark, Microscope, HardHat, Hammer, Sun,
} from "lucide-react";
import { fetchAreas, fetchInitiatives, fetchPartners } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";

const ICONS = {
  Sprout, Wheat, Bird, Mountain, Flame, Users, BookOpen, Microscope, Sun,
  Feather, Compass, Tractor, Leaf, Landmark, HardHat, Hammer,
};

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/4339aed2-4f84-44c3-91d1-62da83bc0bdc/images/519adf03eac96967e3e71fc6499607e7096f2857790b638b5b228e4ccf1994be.png";
const COUNTRY_IMG = "https://customer-assets.emergentagent.com/job_green-workforce-1/artifacts/su2qw556_desert-fire-jeep.jpeg";

const ECONOMY_PILLARS = [
  "Native seed supply",
  "Indigenous land & sea management",
  "Biodiversity markets",
  "Carbon projects",
  "Ecological monitoring",
  "Regenerative agriculture",
  "Habitat recovery",
  "Cultural tourism",
];

export default function HomePage() {
  const [areas, setAreas] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    fetchAreas().then(setAreas).catch(() => {});
    fetchInitiatives().then(setInitiatives).catch(() => {});
    fetchPartners().then(setPartners).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden ra-grain border-b border-ra-line">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.55]"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ra-bone/70 via-ra-bone/40 to-ra-bone" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24 lg:pb-36 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-10 ra-fade-up">
            <div className="ra-overline">Restoration Australia — Building the systems behind restoration</div>
            <h1 className="ra-serif mt-6 text-4xl sm:text-5xl lg:text-[4.5rem] xl:text-[5.5rem] leading-[1.02] tracking-tight text-ra-eucalypt">
              Restoring Australia's<br />
              <span className="italic text-ra-ochre">natural systems</span>.
            </h1>
            <p className="ra-serif mt-6 text-xl sm:text-2xl lg:text-3xl text-ra-eucalypt leading-snug max-w-4xl">
              Building the knowledge, infrastructure, partnerships and investment needed for
              restoration at scale.
            </p>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl">
              <p className="text-base lg:text-lg text-ra-eucalypt leading-relaxed">
                Australia's restoration challenge is not simply ecological. It is cultural,
                economic and institutional.
              </p>
              <p className="text-base lg:text-lg text-ra-soft leading-relaxed">
                Restoration Australia explores the ideas, partnerships and practical solutions
                needed to restore landscapes, strengthen communities and support long-term
                stewardship of Country.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/initiatives" className="ra-btn-primary" data-testid="hero-initiatives-cta">
                See our initiatives <ArrowRight size={16} />
              </Link>
              <Link to="/about" className="ra-btn-ghost" data-testid="hero-about-cta">
                About the platform
              </Link>
            </div>
          </div>
        </div>

        {/* Stat strip */}
        <div className="relative border-t border-ra-line bg-ra-bone/95 backdrop-blur">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              ["08", "Areas of focus"],
              ["03", "Active initiatives"],
              ["08", "Partner communities"],
              ["AU", "National in scope"],
            ].map(([n, l]) => (
              <div key={l} className="flex flex-col gap-1">
                <div className="ra-serif text-4xl text-ra-ochre">{n}</div>
                <div className="ra-overline text-ra-soft">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE RESTORATION ECONOMY */}
      <section data-testid="section-restoration-economy" className="bg-ra-bone border-b border-ra-line">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid grid-cols-12 gap-12 items-start">
            <div className="col-span-12 lg:col-span-5">
              <div className="ra-overline">The restoration economy</div>
              <h2 className="ra-serif text-4xl sm:text-5xl lg:text-[3.5rem] mt-5 leading-[1.05]">
                One of Australia's most important <span className="italic text-ra-ochre">emerging industries</span>.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-7 space-y-6 text-ra-eucalypt">
              <p className="text-lg leading-relaxed">
                Restoration is becoming one of Australia's most important emerging industries.
                Native seed supply, Indigenous land and sea management, biodiversity markets,
                carbon projects, ecological monitoring, regenerative agriculture, habitat recovery
                and cultural tourism are creating new opportunities across regional Australia.
              </p>
              <p className="text-ra-soft leading-relaxed">
                Yet significant gaps remain. Australia requires stronger restoration
                infrastructure, coordinated seed systems, skilled workforces, improved data
                systems and long-term investment models capable of supporting restoration at
                landscape scale.
              </p>
              <p className="ra-serif text-2xl lg:text-3xl italic text-ra-eucalypt pt-3 leading-snug">
                Restoration Australia exists to help accelerate that conversation.
              </p>
            </div>
          </div>

          {/* Economy pillars marquee */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-ra-line border border-ra-line">
            {ECONOMY_PILLARS.map((p, i) => (
              <div
                key={p}
                data-testid={`economy-pillar-${i + 1}`}
                className="bg-ra-bone p-6 lg:p-7 flex items-center justify-between hover:bg-ra-paper transition-colors duration-300"
              >
                <span className="ra-serif text-lg lg:text-xl text-ra-eucalypt">{p}</span>
                <span className="ra-overline text-ra-light">0{i + 1}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CARING FOR COUNTRY */}
      <section data-testid="section-caring-for-country" className="bg-ra-eucalypt text-ra-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-12 gap-12 items-center">
          <div className="col-span-12 lg:col-span-6 order-2 lg:order-1">
            <div className="ra-overline text-ra-ochre">Indigenous leadership</div>
            <h2 className="ra-serif text-4xl sm:text-5xl lg:text-[3.5rem] mt-5 leading-[1.05] text-ra-bone">
              Caring for Country <span className="italic text-ra-sand">is restoration</span>.
            </h2>
            <div className="mt-7 space-y-5 text-ra-bone/85">
              <p className="text-lg leading-relaxed">
                For more than 65,000 years, First Nations peoples have cared for Australia's
                lands, waters and ecosystems.
              </p>
              <p className="leading-relaxed">
                Today, Indigenous Protected Areas, ranger programs, cultural fire management,
                native seed enterprises, biodiversity stewardship and cultural knowledge systems
                are helping shape the future of environmental management.
              </p>
              <p className="leading-relaxed">
                As Australia responds to biodiversity loss and climate change, Indigenous
                leadership will remain central to successful restoration outcomes.
              </p>
              <p className="leading-relaxed">
                Restoration Australia supports Indigenous-led approaches that combine cultural
                knowledge, community aspirations and contemporary science.
              </p>
              <p className="ra-serif italic text-2xl lg:text-3xl text-ra-sand pt-3 leading-snug">
                Restoration must be understood as more than environmental management. It is
                cultural authority, economic participation and long-term stewardship.
              </p>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-6 order-1 lg:order-2 relative">
            <div className="aspect-[4/5] overflow-hidden border border-ra-bone/15">
              <img src={COUNTRY_IMG} alt="Caring for Country" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:block absolute -bottom-6 -left-4 lg:-left-10 bg-ra-bone text-ra-eucalypt border border-ra-line p-6 max-w-xs">
              <div className="ra-overline">Field note</div>
              <p className="ra-serif text-xl mt-2 leading-snug">
                "Always was, always will be Aboriginal land."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* KNOWLEDGE INFRASTRUCTURE */}
      <section data-testid="section-knowledge-infrastructure" className="bg-ra-paper border-y border-ra-line">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-12 gap-12 items-start">
          <div className="col-span-12 lg:col-span-5">
            <div className="ra-overline">Knowledge infrastructure</div>
            <h2 className="ra-serif text-4xl sm:text-5xl lg:text-[3.5rem] mt-5 leading-[1.05]">
              Restoration requires <span className="italic text-ra-ochre">better information</span>.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-7 space-y-6 text-ra-eucalypt">
            <p className="text-lg leading-relaxed">Good decisions depend on good information.</p>
            <p className="text-ra-soft leading-relaxed">
              Across Australia, Traditional Owners, researchers, land managers and community
              organisations are generating valuable ecological and cultural knowledge. Yet much of
              this information remains fragmented across projects, agencies and regions.
            </p>
            <p className="text-ra-soft leading-relaxed">
              Emerging Indigenous-led monitoring systems demonstrate how local knowledge, cultural
              governance and modern information systems can work together to strengthen both
              environmental outcomes and community decision making.
            </p>
            <p className="ra-serif text-2xl lg:text-3xl italic text-ra-eucalypt pt-3 leading-snug">
              We place data sovereignty, community control and practical stewardship at the
              centre of restoration planning.
            </p>
          </div>
        </div>
      </section>

      {/* AREAS OF FOCUS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-12 gap-10 items-end mb-14">
          <div className="col-span-12 lg:col-span-7">
            <SectionHeader
              overline="Our focus"
              title={<>Eight interconnected <span className="italic text-ra-ochre">systems</span>.</>}
              lede="The systems where Restoration Australia explores ideas, partnerships and practical solutions — from native seed and Caring for Country to climate adaptation, finance and regional capacity."
              testId="home-areas-header"
            />
          </div>
          <div className="hidden lg:flex col-span-5 justify-end">
            <Link to="/focus" data-testid="home-areas-view-all" className="ra-link text-sm tracking-[0.18em] uppercase">
              View all areas →
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ra-line border border-ra-line ra-stagger">
          {areas.map((a) => {
            const Icon = ICONS[a.icon] || Sprout;
            return (
              <div
                key={a.id}
                data-testid={`area-card-${a.slug}`}
                className="group bg-ra-bone p-7 lg:p-9 min-h-[230px] flex flex-col justify-between transition-colors duration-300 hover:bg-ra-paper cursor-default"
              >
                <Icon size={28} className="text-ra-ochre transition-transform duration-500 group-hover:rotate-6" strokeWidth={1.4} />
                <div>
                  <div className="ra-overline text-ra-soft mb-2">0{a.order}</div>
                  <h3 className="ra-serif text-2xl lg:text-3xl leading-tight">{a.title}</h3>
                  <p className="mt-3 text-sm text-ra-soft leading-relaxed line-clamp-3">{a.summary}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* INITIATIVES */}
      <section className="bg-ra-paper border-y border-ra-line">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <SectionHeader
            overline="Current initiatives"
            title={<>Programmes <span className="italic text-ra-ochre">on the ground</span>.</>}
            lede="Three flagship initiatives currently delivering across the continent."
            testId="home-initiatives-header"
          />

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-px bg-ra-line border border-ra-line">
            {initiatives.map((i) => (
              <Link
                key={i.id}
                to={`/initiatives/${i.slug}`}
                data-testid={`initiative-card-${i.slug}`}
                className="group bg-ra-bone p-8 lg:p-10 flex flex-col gap-6 transition-all duration-500 hover:bg-ra-eucalypt hover:text-ra-bone lg:col-span-4"
              >
                <div className="aspect-[16/10] overflow-hidden border border-ra-line group-hover:border-ra-bone/30">
                  <img
                    src={i.image_url}
                    alt={i.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 grayscale-[0.08]"
                  />
                </div>
                <div className="flex-1 flex flex-col">
                  <div className="ra-overline group-hover:text-ra-sand">0{i.order} — Initiative</div>
                  <h3 className="ra-serif text-3xl lg:text-4xl mt-3 group-hover:text-ra-bone">{i.name}</h3>
                  <p className="mt-3 text-base italic text-ra-ochre group-hover:text-ra-sand">{i.tagline}</p>
                  <p className="mt-4 text-sm text-ra-soft group-hover:text-ra-bone/80 leading-relaxed line-clamp-4">
                    {i.summary}
                  </p>
                  <div className="mt-6 text-sm uppercase tracking-[0.18em] flex items-center gap-2 group-hover:text-ra-sand">
                    Read more <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="bg-ra-eucalypt text-ra-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid grid-cols-12 gap-10 items-end mb-14">
            <div className="col-span-12 lg:col-span-7">
              <div className="ra-overline text-ra-ochre">Who we work with</div>
              <h2 className="ra-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] text-ra-bone">
                A continent-wide<br />
                <span className="italic text-ra-sand">community of practice</span>.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:text-right">
              <Link to="/partners" data-testid="home-partners-cta" className="text-sm tracking-[0.18em] uppercase border-b border-ra-bone/30 hover:border-ra-ochre hover:text-ra-ochre pb-1 transition-colors">
                Meet our partners →
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ra-bone/10 border border-ra-bone/15 ra-stagger">
            {partners.map((p) => {
              const Icon = ICONS[p.icon] || Users;
              return (
                <div
                  key={p.id}
                  data-testid={`partner-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="bg-ra-eucalypt p-6 lg:p-8 flex flex-col gap-4 min-h-[180px] hover:bg-[#243528] transition-colors"
                >
                  <Icon size={24} className="text-ra-ochre" strokeWidth={1.4} />
                  <div>
                    <h4 className="ra-serif text-xl lg:text-2xl text-ra-bone leading-tight">{p.name}</h4>
                    <p className="text-xs text-ra-bone/65 mt-2 leading-relaxed line-clamp-3">{p.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
        <div className="ra-overline">Get involved</div>
        <h2 className="ra-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] max-w-3xl mx-auto">
          Australia's environmental <span className="italic text-ra-ochre">future</span>.
        </h2>
        <p className="mt-6 text-ra-soft max-w-2xl mx-auto leading-relaxed">
          If you're a Traditional Owner group, landholder, practitioner, researcher, agency or
          investor — we'd like to hear from you.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/contact" data-testid="home-cta-contact" className="ra-btn-primary">
            Start a conversation <ArrowRight size={16} />
          </Link>
          <Link to="/resources" data-testid="home-cta-resources" className="ra-btn-ghost">
            Explore resources
          </Link>
        </div>
      </section>
    </div>
  );
}
